/**
 * UserModal Component
 * Modal for inviting new users or editing existing users
 * Two modes: "invite" for new users, "edit" for existing users
 *
 * @module components/admin/UserModal
 */

import React, { useState, useEffect } from 'react';
import { User, UserRole, InviteUserData, UpdateUserData } from '@/types';
import { logger } from '@/utils/logger';
import Modal from '@/components/common/Modal';
import Button from '@/components/common/Button';

// ============================================================================
// TYPES
// ============================================================================

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'invite' | 'edit';
  user?: User;
  onSubmit: (data: InviteUserData | UpdateUserData) => Promise<void>;
  isLoading?: boolean;
}

interface FormErrors {
  email?: string;
  displayName?: string;
  role?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * User modal component for inviting/editing users
 * Handles form validation and submission
 *
 * @component
 * @example
 * // Invite mode
 * <UserModal
 *   isOpen={isOpen}
 *   mode="invite"
 *   onClose={handleClose}
 *   onSubmit={handleInvite}
 * />
 *
 * @example
 * // Edit mode
 * <UserModal
 *   isOpen={isOpen}
 *   mode="edit"
 *   user={selectedUser}
 *   onClose={handleClose}
 *   onSubmit={handleUpdate}
 * />
 */
const UserModal: React.FC<UserModalProps> = ({
  isOpen,
  onClose,
  mode,
  user,
  onSubmit,
  isLoading = false,
}) => {
  // Form state
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<UserRole>('member');
  const [errors, setErrors] = useState<FormErrors>({});

  // Debug logging
  useEffect(() => {
    logger.debug('[WALRUS] UserModal: mounted', {
      mode,
      isOpen,
      userId: user?.id,
    });
  }, [mode, isOpen, user?.id]);

  // Initialize form when modal opens or user changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && user) {
        setEmail(user.email);
        setDisplayName(user.displayName || '');
        setRole(user.role);
        logger.debug('[WALRUS] UserModal: initialized form for editing', { userId: user.id });
      } else if (mode === 'invite') {
        setEmail('');
        setDisplayName('');
        setRole('member');
        logger.debug('[WALRUS] UserModal: initialized form for inviting');
      }
      setErrors({});
    }
  }, [isOpen, mode, user]);

  /**
   * Validate email format
   */
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  /**
   * Validate form
   */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (mode === 'invite') {
      if (!email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!isValidEmail(email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    if (displayName.trim().length > 100) {
      newErrors.displayName = 'Display name must be 100 characters or less';
    }

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;

    logger.debug('[WALRUS] UserModal: form validation', { isValid, errors: newErrors });
    return isValid;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    logger.debug('[WALRUS] UserModal: form submitted', { mode });

    if (!validateForm()) {
      return;
    }

    try {
      if (mode === 'invite') {
        const inviteData: InviteUserData = {
          email,
          displayName: displayName.trim() || undefined,
          role,
        };
        logger.debug('[WALRUS] UserModal: submitting invite', { email });
        await onSubmit(inviteData);
      } else if (mode === 'edit' && user) {
        const updateData: UpdateUserData = {
          displayName: displayName.trim() || undefined,
          role,
        };
        logger.debug('[WALRUS] UserModal: submitting update', { userId: user.id });
        await onSubmit(updateData);
      }

      // Close modal on success
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error('[WALRUS] UserModal: submission failed', { error: errorMessage });
      setErrors({ ...errors, email: errorMessage });
    }
  };

  const title = mode === 'invite' ? 'Invite New User' : 'Edit User';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email field */}
        {mode === 'invite' && (
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-walrus-white mb-2">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) {
                  setErrors({ ...errors, email: undefined });
                }
              }}
              placeholder="user@example.com"
              disabled={isLoading}
              className={`w-full rounded-lg border px-4 py-2 text-walrus-white placeholder-walrus-silver transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-walrus-charcoal ${
                errors.email
                  ? 'border-red-500 bg-walrus-charcoal focus:ring-red-500'
                  : 'border-walrus-gray bg-walrus-charcoal focus:border-white focus:ring-white'
              }`}
              required
            />
            {errors.email && <p className="mt-1 text-sm text-red-400">{errors.email}</p>}
          </div>
        )}

        {/* Display Name field */}
        <div>
          <label htmlFor="displayName" className="block text-sm font-medium text-walrus-white mb-2">
            Display Name
          </label>
          <input
            id="displayName"
            type="text"
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
              if (errors.displayName) {
                setErrors({ ...errors, displayName: undefined });
              }
            }}
            placeholder="John Doe"
            disabled={isLoading}
            maxLength={100}
            className={`w-full rounded-lg border px-4 py-2 text-walrus-white placeholder-walrus-silver transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-walrus-charcoal ${
              errors.displayName
                ? 'border-red-500 bg-walrus-charcoal focus:ring-red-500'
                : 'border-walrus-gray bg-walrus-charcoal focus:border-white focus:ring-white'
            }`}
          />
          <p className="mt-1 text-xs text-walrus-silver">
            {displayName.length}/100
          </p>
          {errors.displayName && <p className="mt-1 text-sm text-red-400">{errors.displayName}</p>}
        </div>

        {/* Role field */}
        <div>
          <label htmlFor="role" className="block text-sm font-medium text-walrus-white mb-2">
            Role
          </label>
          <select
            id="role"
            value={role}
            onChange={(e) => {
              setRole(e.target.value as UserRole);
              if (errors.role) {
                setErrors({ ...errors, role: undefined });
              }
            }}
            disabled={isLoading}
            className="w-full rounded-lg border border-walrus-gray bg-walrus-charcoal px-4 py-2 text-walrus-white transition-colors focus:border-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-walrus-charcoal focus:ring-white"
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
          {errors.role && <p className="mt-1 text-sm text-red-400">{errors.role}</p>}
        </div>

        {/* Info text */}
        <div className="rounded-lg bg-blue-900/20 border border-blue-800 p-3">
          <p className="text-xs text-blue-200">
            {mode === 'invite'
              ? 'An invitation email will be sent to the user with instructions to set up their account.'
              : 'Changes will be applied immediately.'}
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3 justify-end pt-4">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            loading={isLoading}
          >
            {mode === 'invite' ? 'Send Invite' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

UserModal.displayName = 'UserModal';

export default UserModal;
